const toNumber = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toDateValue = (value) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

const sameOrBeforeDay = (lhs, rhs) => {
    return lhs.getFullYear() < rhs.getFullYear()
        || (lhs.getFullYear() === rhs.getFullYear() && lhs.getMonth() < rhs.getMonth())
        || (lhs.getFullYear() === rhs.getFullYear() && lhs.getMonth() === rhs.getMonth() && lhs.getDate() <= rhs.getDate());
};

export const getActiveSystemFuelSurchargeRate = (rates = [], targetDate = new Date()) => {
    const dateToUse = toDateValue(targetDate) || new Date();

    const validRates = rates
        .map((rate) => {
            const fromDate = toDateValue(rate?.fs_from);
            if (!fromDate) {
                return null;
            }

            return {
                ...rate,
                _fromDate: fromDate,
                fs_percent: toNumber(rate?.fs_percent, 0),
            };
        })
        .filter((rate) => rate && sameOrBeforeDay(rate._fromDate, dateToUse))
        .sort((a, b) => b._fromDate.getTime() - a._fromDate.getTime());

    if (validRates.length === 0) {
        return {
            percent: 0,
            hasSystemRate: false,
            systemRateRow: null,
        };
    }

    return {
        percent: validRates[0].fs_percent,
        hasSystemRate: validRates[0].fs_percent > 0,
        systemRateRow: validRates[0],
    };
};

export const getActiveClientFuelSurchargeRate = ({
    clientKey,
    rates = [],
    targetDate = new Date(),
}) => {
    const dateToUse = toDateValue(targetDate) || new Date();

    const validRates = rates
        .filter((rate) => !clientKey || rate?.clientKey === clientKey)
        .map((rate) => {
            const fromDate = toDateValue(rate?.fsc_from);
            if (!fromDate) {
                return null;
            }

            return {
                ...rate,
                _fromDate: fromDate,
                fsc_percent: toNumber(rate?.fsc_percent, 0),
            };
        })
        .filter((rate) => rate && sameOrBeforeDay(rate._fromDate, dateToUse))
        .sort((a, b) => b._fromDate.getTime() - a._fromDate.getTime());

    if (validRates.length === 0) {
        return {
            percent: 0,
            hasClientRate: false,
            clientRateRow: null,
        };
    }

    return {
        percent: validRates[0].fsc_percent,
        hasClientRate: true,
        clientRateRow: validRates[0],
    };
};

export const getEffectiveFuelSurchargeRate = ({ client = {}, systemPercent = 0, clientKey = null, customRates = [], targetDate = new Date() }) => {
    const resolvedSystemPercent = toNumber(systemPercent, 0);

    // Custom client rates only apply when an active system rate is above 0.
    if (resolvedSystemPercent <= 0) {
        return {
            percent: 0,
            source: "none",
            systemPercent: 0,
            customEnabled: false,
            customPercent: 0,
        };
    }

    const customEnabledValue = client?.fuel_surcharge_custom_enabled ?? 0;
    const customEnabled = `${customEnabledValue}` === "1";

    if (!customEnabled) {
        return {
            percent: resolvedSystemPercent,
            source: "system",
            systemPercent: resolvedSystemPercent,
            customEnabled: false,
            customPercent: 0,
        };
    }

    const resolvedClientKey = clientKey || client?.clientKey || null;
    const activeClientCustomRate = getActiveClientFuelSurchargeRate({
        clientKey: resolvedClientKey,
        rates: customRates,
        targetDate,
    });

    if (activeClientCustomRate.hasClientRate) {
        return {
            percent: activeClientCustomRate.percent,
            source: "client_custom",
            systemPercent: resolvedSystemPercent,
            customEnabled: true,
            customPercent: activeClientCustomRate.percent,
            customRateRow: activeClientCustomRate.clientRateRow,
        };
    }

    return {
        percent: resolvedSystemPercent,
        source: "system",
        systemPercent: resolvedSystemPercent,
        customEnabled: false,
        customPercent: 0,
    };
};

export const calculateFuelSurchargeAmount = (basePrice, percent) => {
    const amount = (toNumber(basePrice, 0) / 100) * toNumber(percent, 0);
    return parseFloat(amount.toFixed(2));
};

export const buildFuelSurchargeChargeItem = ({ basePrice, percent, created, userKey }) => {
    const amount = calculateFuelSurchargeAmount(basePrice, percent);

    return {
        key: `AUTO-FS-${created || new Date().toISOString()}`,
        chargeCode: "FUEL SURCHARGE",
        autoType: "fuel_surcharge",
        created,
        userKey,
        percent: parseFloat(toNumber(percent, 0).toFixed(2)),
        price: amount,
    };
};
